import { Influencer, InfluencerListResponse, InfluencerAudienceAge } from '../types/influencer';
import { FilterValues } from '../components/filters/FilterSection';
import influencerCsvContent from '../data/influencers.csv';

// Helper function to parse CSV data
const parseCsvData = (csvString: string): Influencer[] => {
  // console.log('Raw csvString received by parseCsvData:', csvString);
  const lines = csvString.trim().split('\n');
  // console.log('Number of lines after split in parseCsvData:', lines.length);

  if (lines.length < 2) {
    //console.error('parseCsvData: Not enough lines to parse, returning empty. Lines found:', lines.length);
    return []; // Not enough data (empty or only header)
  }

  const headers = lines[0].split(';').map(header => header.trim());
  const dataRows = lines.slice(1);

  return dataRows.map(line => {
    const values = line.split(';').map(value => {
      // Remove leading/trailing quotes if they exist and trim
      let processedValue = value.trim();
      if (processedValue.startsWith('"') && processedValue.endsWith('"')) {
        processedValue = processedValue.substring(1, processedValue.length - 1);
      }
      // Replace '''' with ' for descriptions that might have escaped quotes from parsing
      return processedValue.replace(/ '''' /g, "'");
    });
    
    const rowObject: any = {};
    headers.forEach((header, index) => {
      rowObject[header] = values[index] !== undefined ? values[index] : ""; // Ensure value is at least an empty string
    });
    return rowObject;
  });
};

let influencerDataPromise: Promise<any[]> | null = null;

const loadInfluencerData = async (): Promise<any[]> => {
  if (influencerDataPromise) {
    return influencerDataPromise;
  }

  // influencerCsvContent is the path to the CSV file
  const csvPath = influencerCsvContent; 
  try {
    // console.log('Attempting to fetch CSV from path:', csvPath);
    const response = await fetch(csvPath);
    if (!response.ok) {
      // console.error('Failed to fetch CSV:', response.status, response.statusText);
      throw new Error(`Failed to fetch CSV: ${response.statusText}`);
    }
    const csvString = await response.text();
    // console.log('Successfully fetched CSV string. Length:', csvString.length);
    if (!csvString) {
      // console.error('Fetched CSV string is empty.');
      return [];
    }
    const parsedData = parseCsvData(csvString);
    // console.log(`Parsed ${parsedData.length} influencers from fetched CSV.`);
    return parsedData;
  } catch (error) {
    //console.error('Error loading or parsing influencer CSV data:', error);
    return []; // Return empty array on error
  }
};

// Call loadInfluencerData once and store its promise
// This will start loading the data as soon as the module is initialized.
// Components/services using this can await this promise.
influencerDataPromise = loadInfluencerData();

// Industry mapping (UI values to database codes)
const INDUSTRY_MAPPING: Record<string, string> = {
  // Direct mappings from UI values to database codes
  'beauty-cosmetics': 'beauty',
  'consumer-electronics': 'tech',
  'food-beverage': 'food',
  'other': 'other'
};

// Country mapping (UI values to ISO codes)
const COUNTRY_MAPPING: Record<string, string> = {
  // Direct mappings from UI values to ISO codes
  'uk': 'GB',
  'germany': 'DE',
  'italy': 'IT',
  'united-states': 'US',
  'other': 'other'
};

// Budget mapping (UI values to numeric lower and upper bounds)
const BUDGET_MAPPING: Record<string, {min: number, max: number}> = {
  // Current mappings
  '0-3000': {min: 0, max: 2999},
  '3000-5000': {min: 3000, max: 4999},
  '5000-10000': {min: 5000, max: 9999},
  '10000+': {min: 10000, max: Infinity},
  
  // Legacy formats for backward compatibility
  '5000-15000': {min: 5000, max: 14999},
  '15000-25000': {min: 15000, max: 24999},
  '25000-50000': {min: 25000, max: 49999},
  '50000+': {min: 50000, max: Infinity},
  '10000-15000': {min: 10000, max: 14999},
  
  // Currency formats with symbols (EUR)
  '€3,000 - €5,000': {min: 3000, max: 4999},
  '€5,000 - €10,000': {min: 5000, max: 9999},
  '€10,000 - €15,000': {min: 10000, max: 14999},
  '€15,000 - €25,000': {min: 15000, max: 24999}
};

// New function to filter influencers directly by the 'budget' column from CSV data against a given range
const filterInfluencersByDirectBudget = (rowsToFilter: any[], budgetRange: {min: number, max: number}): any[] => {
  if (!budgetRange) {
    return rowsToFilter;
  }
  // console.log(`Applying direct budget filter. Range: min=${budgetRange.min}, max=${budgetRange.max}. Rows in: ${rowsToFilter.length}`);
  const result = rowsToFilter.filter(row => {
    if (typeof row.budget !== 'string') { // Assuming 'budget' comes from CSV as string
      return false;
    }
    const influencerBudgetVal = parseInt(row.budget.trim(), 10);
    if (isNaN(influencerBudgetVal)) {
      return false;
    }
    return influencerBudgetVal >= budgetRange.min && influencerBudgetVal <= budgetRange.max;
  });
  // console.log(`Direct budget filter result. Rows out: ${result.length}`);
  return result;
};

// Map age code to age range
const getAgeRangeFromCode = (ageCode: number): InfluencerAudienceAge => {
  switch (ageCode) {
    case 13:
      return { '13-17': 100, '18-24': 0, '25-34': 0, '35-44': 0, '45-54': 0, '55+': 0 };
    case 18:
      return { '13-17': 0, '18-24': 100, '25-34': 0, '35-44': 0, '45-54': 0, '55+': 0 };
    case 25:
      return { '13-17': 0, '18-24': 0, '25-34': 100, '35-44': 0, '45-54': 0, '55+': 0 };
    case 35:
      return { '13-17': 0, '18-24': 0, '25-34': 0, '35-44': 100, '45-54': 0, '55+': 0 };
    case 45:
      return { '13-17': 0, '18-24': 0, '25-34': 0, '35-44': 0, '45-54': 100, '55+': 0 };
    default:
      // Default to 25-34 if unknown
      return { '13-17': 0, '18-24': 0, '25-34': 100, '35-44': 0, '45-54': 0, '55+': 0 };
  }
};

// Convert a data row to an Influencer object
const rowToInfluencer = (row: any): Influencer => {
  // Get audience age distribution from age code
  const ageCode = parseInt(row.age, 10);
  const audienceAge = getAgeRangeFromCode(ageCode);
  
  // Handle interests as a single unit
  const interestsString = row.interests ? row.interests.trim() : "";
  const interestsArray = interestsString ? 
    [{ name: interestsString, percentage: 100 }] : // Assume 100% for the single interest
    [{ name: 'General', percentage: 80 }]; // Default if no interest is specified
  
  const result: Influencer = {
    id: row.id,
    name: row.name,
    username: row.username,
    profileImage: row.profile_pic_url,
    platform: 'instagram',
    followers: parseInt(row.followers, 10),
    instagramUrl: row.ig_url,
    tiktokUrl: row.tt_url,
    engagementRate: parseFloat(row.engagement_rate),
    audienceGender: {
      male: parseInt(row.male, 10) || 0,
      female: parseInt(row.female, 10) || 0
    },
    audienceAge,
    audienceInterests: interestsArray,
    rating: {
      aiMatch: parseInt(row.ai_match_score, 10) || 70,
      totalMatch: Math.round(parseInt(row.ai_match_score, 10) / 20) || 3, // Convert to 0-5 scale
      matchDescription: row.ai_match_desc || 'Average match for your criteria'
    },
    matchScore: parseInt(row.ai_match_score, 10) || 70 // Also ensure matchScore is populated
  };
  return result;
};

export const CSVDataService = {
  /**
   * Get influencers based on filter criteria
   */
  async getInfluencers(
    filters: any, // Changed from FilterValues to any to easily access minBudget/maxBudget
    clientIp: string = '127.0.0.1'
  ): Promise<InfluencerListResponse> {
    // console.log('Fetching influencers with filters:', JSON.stringify(filters, null, 2));
    
    try {
      let rows = await influencerDataPromise;
      if (!rows || rows.length === 0) {
        return { influencers: [], total: 0 };
      }
      
      // Determine budget range for filtering
      let budgetRangeForFiltering: { min: number; max: number } | undefined = undefined;
      if (filters.minBudget !== undefined && filters.maxBudget !== undefined) {
        budgetRangeForFiltering = { min: filters.minBudget, max: filters.maxBudget };
      } else if (filters.budget) {
        // Fallback to using BUDGET_MAPPING if old budget string key is present
        budgetRangeForFiltering = BUDGET_MAPPING[filters.budget];
      }

      // console.log('Budget range for filtering:', budgetRangeForFiltering);

      const dbFilters = {
        industry: filters.industry ? INDUSTRY_MAPPING[filters.industry] || filters.industry : undefined,
        country: filters.country ? COUNTRY_MAPPING[filters.country] || filters.country : undefined,
        // budget is now handled by budgetRangeForFiltering directly
      };
      
      let filteredRows: any[] = [...rows];
      
      if (dbFilters.country && dbFilters.country !== 'other') {
        filteredRows = filteredRows.filter(row => row.country === dbFilters.country);
      }
      
      if (dbFilters.industry && dbFilters.industry !== 'other') {
        filteredRows = filteredRows.filter(row => row.industry === dbFilters.industry);
      }
      
      // Apply direct budget filter using the determined range
      if (budgetRangeForFiltering) {
        // console.log('Applying direct budget filter with range:', budgetRangeForFiltering);
        filteredRows = filterInfluencersByDirectBudget(filteredRows, budgetRangeForFiltering);
        // console.log(`After direct budget filter: ${filteredRows.length} influencers remain.`);
      }
      
      filteredRows.sort((a, b) => parseInt(b.ai_match_score, 10) - parseInt(a.ai_match_score, 10));
      
      // Convert rows to Influencer objects
      const influencers = filteredRows.map(row => {
        const influencer = rowToInfluencer(row);
        return influencer;
      });
      
      return {
        influencers,
        total: influencers.length,
      };
    } catch (error) {
      //console.error('Error processing data in getInfluencers:', error);
      return { influencers: [], total: 0 };
    }
  },

  /**
   * Save user email and preferences - just a stub since we're not using a database
   */
  async saveUserInfo(email: string, filters: FilterValues): Promise<{ success: boolean }> {
    // console.log('Saving user info (CSV mode - this is just logged, not saved)');
    // console.log('Email:', email);
    // console.log('Filters:', filters);
    return { success: true };
  }
};

export default CSVDataService; 